<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      ref="tableRemoteRef"
      :headers="tableHeaders"
      :items="displayedFeatures"
      :pagination="pagination"
      :isSearchable="false"
      :isSelectable="false"
      :itemActions="actions"
      :isCustomIcon="true"
      :title="$t('features.pageName')"

      hideDefaultFooter
      @iconAction="onClickAction"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('features.searchBy')"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
      </template>
      <template v-slot:item.price="{ item }">
        <span>{{ item.price !== null && item.price !== undefined ? `${item.price} $` : '' }}</span>
      </template>
      <template v-slot:item.description="{ item }">
        <span>{{ item.description ? (item.description[$i18n.locale] || item.description[teamMainLang] || '') : '' }}</span>
      </template>
      <template v-slot:item.active="{ item }">
        <v-switch
          readonly
          :inputValue="item.globalActivationStatus"
          @click.stop="handleToggleClick(item)"
        />
      </template>

      <template v-slot:item.createdAt="{ item }">
        <span>{{ formatDate(item.createdAt) }}</span>
      </template>
      <template v-slot:item.updatedAt="{ item }">
        <span>{{ formatDate(item.updatedAt) }}</span>
      </template>

      <template v-slot:item.activationDate="{ item }">
        <span>{{ formatDate(item.activationDate) }}</span>
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import FeatureForm from '@/components/forms/FeatureForm.vue';
import FeatureActivationForm from '@/components/forms/FeatureActivationForm.vue';

export default {
    components: {
        TableRemote,
        SearchWithFilters,
    },
    data() {
        return {
            allHeadersTitles: [
                'key',
                'type',
                'description',
                'price',
                'createdAt',
                'updatedAt',
                'activationDate',
                'active',
            ],
            isInitializing: false,
            items: [],
            pagination: {},
            queryParams: {},
            filterName: 'features',
        };
    },
    computed: {
        ...mapGetters([
            'teamID',
            'teamMainLang',
        ]),
        tableHeaders() {
            const headers = this.allHeadersTitles.map(header => ({
                text: this.$t(`features.headers.${header}`),
                value: header,
                sortable: header !== 'accessGroups',
            }));
            headers.unshift({
                text: '',
                value: 'icon',
                sortable: false,
            });
            return headers;
        },
        actions() {
            const actions = [
                {
                    name: this.$t('features.edit'),
                    icon: 'edit',
                    mode: 'edit',
                },
            ];
            return actions;
        },
        /**
         * Returns a new array of features with the active and activationDate properties added.
         * The active property is true if at least one feature has globalActivationStatus === true.
         * The activationDate property is the activatedAt value of the first matching feature.
         */
        displayedFeatures() {
            return this.items.map(item => {
                const isActive = item.globalActivationStatus === true;
                return {
                    ...item,
                    active: isActive,
                    activationDate: isActive ? item.activationDate : null,
                };
            });
        },

    },
    async created() {
        this.isInitializing = true;
        await this.loadFeatures();
        this.isInitializing = false;
    },
    methods: {
        async loadFeatures() {
            try {
                const features = await this.$rq.getSuperAdminFeatures(this.queryParams);
                this.items = features.data;
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: this.$t('features.msg.loadError'),
                });
            }
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.items = [];
            this.loadFeatures();
        },
        handleToggleClick(item) {
            this.featureToToggle = item.key;
            this.newActiveState = !item.active;
            const { title, message, additionalMessage } = this.getConfirmationMessages(this.newActiveState);
            this.$root.$emit('showModal', {
                title,
                component: Vue.component('FeatureActivationForm', FeatureActivationForm),
                payload: {
                    msg: message,
                    additionalMsg: additionalMessage,
                },
                onConfirm: () => this.confirmToggle(),
                isForm: false,
            });
        },
        getConfirmationMessages(isActivating) {
            return {
                title: isActivating ? this.$t('features.confirmActivateTitle') : this.$t('features.confirmDeactivateTitle'),
                message: isActivating ? this.$t('features.confirmActivateMessage') : this.$t('features.confirmDeactivateMessage'),
                additionalMessage: isActivating
                    ? this.$t('features.confirmActivateAdditionalMessage')
                    : this.$t('features.confirmDeactivateAdditionalMessage'),
            };
        },
        async confirmToggle() {
            try {
                const featureKey = this.featureToToggle;
                const featureToToggle = this.items.find(
                    item => item.key === featureKey,
                );
                await this.toggleFeatureActive(featureToToggle, !this.newActiveState);
                this.featureToToggle = null;
                this.$toasted.global.toast_success({
                    message: this.newActiveState
                        ? this.$t('features.msg.featureActivated', { data: featureKey })
                        : this.$t('features.msg.featureDeactivated', { data: featureKey }),
                });
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: this.$t('features.msg.toggleError'),
                });
            }
        },

        async toggleFeatureActive(item, newState) {
            const isActivating = Boolean(newState);
            await this.$rq.superAdminToggleFeature(item.id, this.teamID, isActivating);
            await this.loadFeatures();
        },

        formatDate(timestamp) {
            return timestamp ? this.$options.filters.formatDate(new Date(timestamp)) : '';
        },
        async onClickAction(item, action) {
            if (action.mode === 'edit') {
                this.$root.$emit('showModal', {
                    width: 750,
                    title: this.$t('features.editFeature'),
                    component: FeatureForm,
                    showLangs: true,
                    payload: { feature: { ...item } },
                    onConfirm: updatedFeature => this.updateFeature({ ...item, ...updatedFeature }),
                });
            }
        },
        async updateFeature(updatedFeature) {
            try {
                const payload = {
                    key: updatedFeature.key,
                    type: updatedFeature.type,
                    description: updatedFeature.description,
                    price: updatedFeature.price,
                };
                await this.$rq.updateFeature(updatedFeature.id, payload);
                this.$toasted.global.toast_success({
                    message: this.$t('features.msg.featureUpdated', { data: updatedFeature.key }),
                });
                await this.loadFeatures();
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: this.$t('features.msg.updateError'),
                });
            }
        },
    },
};
</script>

<style scoped>
::v-deep .action-buttons {
    justify-content: flex-start !important;
}
</style>
